// DateRangeWithPresets.jsx
import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { FormControl, Select, MenuItem, InputLabel, Box } from "@mui/material";
import { DatePicker } from "antd";
import { defaultDateRangeDays } from "../../utils/constant";
const { RangePicker } = DatePicker;

/**
 * Props:
 * - defaultPreset: "7" | "30" | "60" | "custom"  (default "7")
 * - onChange: (start: Dayjs|null, end: Dayjs|null, preset: string) => void
 * - minWidth: number|string for the Select (optional)
 */
export default function DateRangeWithPresets({
    defaultPreset = defaultDateRangeDays,
    onChange,
    minWidth = 220,
    selectedDate = null,
}) {
    const [preset, setPreset] = useState(defaultPreset);
    const [selectedRange, setSelectedRange] = useState([
        dayjs().subtract((parseInt(defaultDateRangeDays) - 1), "day"),
        dayjs(),
    ]);

    // derive range for non-custom presets
    const computedRange = useMemo(() => {
        if (preset === "7") return [dayjs().subtract(6, "day"), dayjs()];
        if (preset === "30") return [dayjs().subtract(29, "day"), dayjs()];
        if (preset === "60") return [dayjs().subtract(59, "day"), dayjs()];
        return selectedRange; // custom uses user-picked range
    }, [preset, selectedRange]);

    // fire external onChange whenever final range changes
    useEffect(() => {
        if (typeof onChange === "function") {
            const [start, end] = computedRange || [null, null];
            onChange(start ?? null, end ?? null, preset);
        }
    }, [computedRange, preset]);

    useEffect(() => {
        handleDateChange(selectedDate);
    }, [selectedDate])

    const handleDateChange = (selectedDate) => {
        // get date difference and set preset for clear button
        if (selectedDate[1].diff(selectedDate[0], 'day') === defaultDateRangeDays) {
            setPreset(defaultDateRangeDays)
        }
    }

    const handlePresetChange = (e) => {
        const value = e.target.value;
        setPreset(value);
        if (value !== "custom") {
            // for presets, we don't show picker; range is auto handled by computedRange
            // ensure we have a defined range for onChange effect
            // (nothing else to do here)
        } else {
            // switching to custom—clear until user picks
            setSelectedRange(null);
        }
    };

    const handleCustomRangeChange = (dates) => {
        // `dates` is [dayjs, dayjs] or null from AntD
        setSelectedRange(dates);
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <FormControl size="small" sx={{ minWidth }}>
                <InputLabel id="drp-preset-label">Date Range</InputLabel>
                <Select
                    labelId="drp-preset-label"
                    label="Date Range"
                    value={preset}
                    onChange={handlePresetChange}
                >
                    <MenuItem value="7">Last 7 days</MenuItem>
                    <MenuItem value="30">Last 30 days</MenuItem>
                    <MenuItem value="60">Last 60 days</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                </Select>
            </FormControl>

            {preset === "custom" && (
                <RangePicker
                    value={selectedRange}
                    onChange={handleCustomRangeChange}
                    allowClear={false}
                    style={{ width: "100%" }}
                />
            )}
        </Box>
    );
}
